import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { API } from '../contexts/AuthContext';

function ipv4Principal(ips) {
  if (!Array.isArray(ips)) return null;
  const entry = ips.find((ip) => ip.family === 'IPv4' && ip.address);
  return entry?.address || null;
}

function baixarVnc(ip, hostname) {
  const conteudo = `[connection]\nhost=${ip}\nport=5900\n`;
  const blob = new Blob([conteudo], { type: 'application/x-vnc' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${hostname}.vnc`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function formatarHora(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
}

function valorOrdenacao(a, campo) {
  switch (campo) {
    case 'nome': return (a.nome_personalizado || a.hostname || '').toLowerCase();
    case 'filial': return a.filial || null;
    case 'mac': return a.mac || null;
    case 'ip': return ipv4Principal(a.ips);
    case 'status': return !!a.online;
    case 'connectedAt': return a.connectedAt ? new Date(a.connectedAt).getTime() : null;
    case 'updatedAt': return a.updatedAt ? new Date(a.updatedAt).getTime() : null;
    default: return null;
  }
}

function ThOrdenavel({ label, campo, sort, onSort, style }) {
  const ativo = sort.campo === campo;
  const icone = ativo
    ? (sort.dir === 'asc' ? 'bi-caret-up-fill' : 'bi-caret-down-fill')
    : 'bi-arrow-down-up';
  return (
    <th
      style={{ ...style, cursor: 'pointer', userSelect: 'none' }}
      onClick={() => onSort(campo)}
      title="Ordenar"
    >
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem' }}>
        {label}
        <i className={`bi ${icone}`} style={{ fontSize: '0.7rem', opacity: ativo ? 0.9 : 0.35 }} />
      </span>
    </th>
  );
}

export default function Agentes() {
  const [agentes, setAgentes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');
  const [filtros, setFiltros] = useState({ nome: '', filial: '', mac: '', ip: '' });
  const [editando, setEditando] = useState(null); // { hostname, valor }
  const [salvando, setSalvando] = useState(null); // hostname em salvamento
  const [pingando, setPingando] = useState(new Set()); // hostnames em ping
  const [pings, setPings] = useState({}); // hostname -> { alive, latency }
  const [atualizando, setAtualizando] = useState(false); // refresh manual em andamento
  const [sort, setSort] = useState({ campo: null, dir: 'asc' });
  const carregando = useRef(false);

  const ordenarPor = (campo) => {
    setSort((prev) => (
      prev.campo === campo
        ? { campo, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { campo, dir: 'asc' }
    ));
  };

  const carregar = useCallback(async (manual = false) => {
    if (carregando.current) return;
    carregando.current = true;
    if (manual) setAtualizando(true);
    try {
      const res = await axios.get(`${API}/agentes`);
      setAgentes(res.data?.data || []);
      setErro('');
    } catch {
      setErro('Não foi possível carregar os agentes.');
    } finally {
      setLoading(false);
      setAtualizando(false);
      carregando.current = false;
    }
  }, []);

  useEffect(() => {
    carregar();
    const id = setInterval(carregar, 10000);
    return () => clearInterval(id);
  }, [carregar]);

  const totalOnline = agentes.filter((a) => a.online).length;

  const algumFiltro = Object.values(filtros).some((v) => v.trim());

  const filtrados = agentes.filter((a) => {
    const nome = `${a.nome_personalizado || ''} ${a.hostname}`.toLowerCase();
    const ipv4 = (a.ips || [])
      .filter((ip) => ip.family === 'IPv4')
      .map((ip) => ip.address)
      .join(' ');
    const checa = (campo, valor) => !valor.trim() || campo.includes(valor.trim().toLowerCase());
    return (
      checa(nome, filtros.nome)
      && checa((a.filial || '').toLowerCase(), filtros.filial)
      && checa((a.mac || '').toLowerCase(), filtros.mac)
      && checa(ipv4.toLowerCase(), filtros.ip)
    );
  });

  const ordenados = sort.campo
    ? [...filtrados].sort((a, b) => {
      const va = valorOrdenacao(a, sort.campo);
      const vb = valorOrdenacao(b, sort.campo);
      if (va == null && vb == null) return 0;
      if (va == null) return 1; // nulos sempre por último
      if (vb == null) return -1;
      let cmp;
      if (typeof va === 'boolean') cmp = va === vb ? 0 : va ? -1 : 1;
      else if (typeof va === 'number') cmp = va - vb;
      else cmp = String(va).localeCompare(String(vb), 'pt-BR', { numeric: true, sensitivity: 'base' });
      return sort.dir === 'asc' ? cmp : -cmp;
    })
    : filtrados;

  const iniciarEdicao = (hostname, nomeAtual) => {
    setEditando({ hostname, valor: nomeAtual || '' });
  };

  const cancelarEdicao = () => setEditando(null);

  const executarPing = async (hostname, ip) => {
    setPingando((prev) => new Set([...prev, hostname]));
    setPings((prev) => ({ ...prev, [hostname]: null }));
    try {
      const res = await axios.post(`${API}/agentes/ping`, { ip });
      const resultado = res.data?.data ?? { alive: false, latency: null };
      setPings((prev) => ({ ...prev, [hostname]: resultado }));
      setTimeout(() => setPings((prev) => {
        const next = { ...prev };
        delete next[hostname];
        return next;
      }), 8000);
    } catch {
      setPings((prev) => ({ ...prev, [hostname]: { alive: false, latency: null } }));
    } finally {
      setPingando((prev) => { const s = new Set(prev); s.delete(hostname); return s; });
    }
  };

  const salvarNome = async (hostname) => {
    setSalvando(hostname);
    try {
      await axios.patch(`${API}/agentes/${encodeURIComponent(hostname)}/nome`, {
        nome_personalizado: editando.valor.trim() || null,
      });
      setEditando(null);
      await carregar();
    } catch {
      setErro('Não foi possível salvar o nome.');
    } finally {
      setSalvando(null);
    }
  };

  return (
    <div className="content-shell">
      <section className="page-header">
        <div>
          <span className="section-header__eyebrow">TI</span>
          <h1>Agentes</h1>
          <p>Máquinas com agente instalado e conectadas ao portal em tempo real.</p>
        </div>
        <div className="page-header__actions">
          <span
            className="permissao-chip permissao-chip--on"
            style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.35rem 0.75rem' }}
          >
            <span style={{
              width: 8, height: 8, borderRadius: '50%',
              background: '#4caf50', display: 'inline-block',
              boxShadow: '0 0 0 2px rgba(76,175,80,0.35)',
            }} />
            {totalOnline} online
          </span>
          {agentes.length > totalOnline && (
            <span
              className="permissao-chip"
              style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.35rem 0.75rem' }}
            >
              <span style={{
                width: 8, height: 8, borderRadius: '50%',
                background: '#9e9e9e', display: 'inline-block',
              }} />
              {agentes.length - totalOnline} offline
            </span>
          )}
          <button type="button" className="btn btn-secondary" onClick={() => carregar(true)} disabled={atualizando}>
            <i className={`bi ${atualizando ? 'bi-arrow-clockwise spin' : 'bi-arrow-clockwise'}`} />
            {atualizando ? ' Atualizando…' : ' Atualizar'}
          </button>
        </div>
      </section>

      <div className="filter-card">
        <div className="filter-card__field filter-card__field--grow">
          <i className="bi bi-pc-display" />
          <input
            type="text"
            placeholder="Nome ou hostname"
            value={filtros.nome}
            onChange={(e) => setFiltros((p) => ({ ...p, nome: e.target.value }))}
          />
        </div>
        <div className="filter-card__field">
          <i className="bi bi-shop" />
          <input
            type="text"
            placeholder="Filial"
            value={filtros.filial}
            onChange={(e) => setFiltros((p) => ({ ...p, filial: e.target.value }))}
          />
        </div>
        <div className="filter-card__field">
          <i className="bi bi-ethernet" />
          <input
            type="text"
            placeholder="MAC"
            value={filtros.mac}
            onChange={(e) => setFiltros((p) => ({ ...p, mac: e.target.value }))}
          />
        </div>
        <div className="filter-card__field">
          <i className="bi bi-hdd-network" />
          <input
            type="text"
            placeholder="IP"
            value={filtros.ip}
            onChange={(e) => setFiltros((p) => ({ ...p, ip: e.target.value }))}
          />
        </div>
        {algumFiltro && (
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            title="Limpar filtros"
            onClick={() => setFiltros({ nome: '', filial: '', mac: '', ip: '' })}
          >
            <i className="bi bi-x-lg" /> Limpar
          </button>
        )}
      </div>

      {erro ? <div className="feedback-banner feedback-banner--error">{erro}</div> : null}

      <section className="content-card">
        {loading && agentes.length === 0 ? (
          <div className="content-card--centered">
            <div className="screen-loader__spinner" />
            <p>Carregando agentes...</p>
          </div>
        ) : filtrados.length === 0 ? (
          <div className="empty-state">
            <i className="bi bi-hdd-network" style={{ fontSize: 32, opacity: 0.4 }} />
            <strong>Nenhum agente cadastrado</strong>
            <span>
              {algumFiltro
                ? 'Nenhuma máquina corresponde aos filtros.'
                : 'Instale e execute o agente nas máquinas para visualizá-las aqui.'}
            </span>
          </div>
        ) : (
          <div className="table-shell">
            <table className="portal-table">
              <thead>
                <tr>
                  <th style={{ width: 24 }} />
                  <ThOrdenavel label="Nome / Hostname" campo="nome" sort={sort} onSort={ordenarPor} />
                  <ThOrdenavel label="Filial" campo="filial" sort={sort} onSort={ordenarPor} />
                  <ThOrdenavel label="MAC" campo="mac" sort={sort} onSort={ordenarPor} />
                  <ThOrdenavel label="Endereços IP" campo="ip" sort={sort} onSort={ordenarPor} />
                  <ThOrdenavel label="Status" campo="status" sort={sort} onSort={ordenarPor} />
                  <ThOrdenavel label="Conectado em" campo="connectedAt" sort={sort} onSort={ordenarPor} />
                  <ThOrdenavel label="Última atualização" campo="updatedAt" sort={sort} onSort={ordenarPor} />
                  <th style={{ width: 130 }}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {ordenados.map((a) => {
                  const ip = ipv4Principal(a.ips);
                  const emEdicao = editando?.hostname === a.hostname;
                  const rowStyle = a.online ? {} : { opacity: 0.55 };

                  return (
                    <tr key={a.hostname} style={rowStyle}>

                      {/* Dot de status */}
                      <td>
                        {a.online ? (
                          <span title="Online" style={{
                            display: 'inline-block', width: 10, height: 10,
                            borderRadius: '50%', background: '#4caf50',
                            boxShadow: '0 0 0 3px rgba(76,175,80,0.25)',
                          }} />
                        ) : (
                          <span title="Offline" style={{
                            display: 'inline-block', width: 10, height: 10,
                            borderRadius: '50%', background: '#9e9e9e',
                          }} />
                        )}
                      </td>

                      {/* Nome / Hostname */}
                      <td>
                        {emEdicao ? (
                          <div style={{ display: 'flex', gap: '0.3rem', alignItems: 'center' }}>
                            <input
                              autoFocus
                              value={editando.valor}
                              onChange={(e) => setEditando((prev) => ({ ...prev, valor: e.target.value }))}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') salvarNome(a.hostname);
                                if (e.key === 'Escape') cancelarEdicao();
                              }}
                              placeholder="Nome personalizado"
                              style={{
                                fontSize: '0.85rem', padding: '0.2rem 0.4rem',
                                border: '1px solid var(--portal-blue)',
                                borderRadius: 4, outline: 'none', width: 160,
                              }}
                            />
                            <button
                              type="button"
                              className="btn btn-primary btn-sm"
                              style={{ padding: '0.2rem 0.45rem' }}
                              title="Salvar"
                              disabled={salvando === a.hostname}
                              onClick={() => salvarNome(a.hostname)}
                            >
                              {salvando === a.hostname
                                ? <i className="bi bi-hourglass-split" />
                                : <i className="bi bi-check-lg" />}
                            </button>
                            <button
                              type="button"
                              className="btn btn-secondary btn-sm"
                              style={{ padding: '0.2rem 0.45rem' }}
                              title="Cancelar"
                              onClick={cancelarEdicao}
                            >
                              <i className="bi bi-x-lg" />
                            </button>
                          </div>
                        ) : (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                            <div>
                              <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>
                                {a.nome_personalizado || (
                                  <span style={{ fontFamily: 'monospace', fontWeight: 500 }}>
                                    {a.hostname}
                                  </span>
                                )}
                              </div>
                              {a.nome_personalizado && (
                                <div style={{ fontFamily: 'monospace', fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                                  {a.hostname}
                                </div>
                              )}
                            </div>
                            <button
                              type="button"
                              title="Editar nome"
                              onClick={() => iniciarEdicao(a.hostname, a.nome_personalizado)}
                              style={{
                                background: 'none', border: 'none', cursor: 'pointer',
                                color: 'var(--text-muted)', padding: '0.15rem 0.25rem',
                                borderRadius: 3, lineHeight: 1,
                              }}
                            >
                              <i className="bi bi-pencil" style={{ fontSize: '0.75rem' }} />
                            </button>
                          </div>
                        )}
                      </td>

                      {/* Filial */}
                      <td>
                        {a.filial
                          ? <span className="cell-strong">{a.filial}</span>
                          : <span className="cell-muted">—</span>}
                      </td>

                      {/* MAC */}
                      <td>
                        {a.mac
                          ? <span style={{ fontFamily: 'monospace', fontSize: '0.82rem' }}>{a.mac}</span>
                          : <span className="cell-muted">—</span>}
                      </td>

                      {/* IPs (somente IPv4) */}
                      <td>
                        {(() => {
                          const ipsV4 = (a.ips || []).filter((ip) => ip.family === 'IPv4');
                          if (ipsV4.length === 0) return <span className="cell-muted">—</span>;
                          return (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
                              {ipsV4.map((ipEntry, idx) => (
                                <span
                                  key={idx}
                                  style={{ fontSize: '0.82rem', display: 'flex', gap: '0.4rem', alignItems: 'center' }}
                                >
                                  <span style={{ color: 'var(--portal-blue)', fontWeight: 500, minWidth: 100 }}>
                                    {ipEntry.interface}
                                  </span>
                                  <span style={{ fontFamily: 'monospace', fontWeight: 500 }}>{ipEntry.address}</span>
                                </span>
                              ))}
                            </div>
                          );
                        })()}
                      </td>

                      {/* Status */}
                      <td>
                        {a.online
                          ? <span className="permissao-chip permissao-chip--on">Online</span>
                          : (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem' }}>
                              <span className="permissao-chip">Offline</span>
                              <i
                                className="bi bi-question-circle"
                                title="O serviço de agente nesta máquina não está conectado ao servidor. Isso não necessariamente significa que a máquina está desligada."
                                style={{ fontSize: '0.8rem', color: 'var(--text-muted)', cursor: 'default' }}
                              />
                            </span>
                          )}
                      </td>

                      {/* Conectado em */}
                      <td className="cell-muted">
                        {a.online ? formatarHora(a.connectedAt) : '—'}
                      </td>

                      {/* Última atualização / última conexão */}
                      <td className="cell-muted">
                        {a.online ? formatarHora(a.updatedAt) : formatarHora(a.updatedAt)}
                      </td>

                      {/* Ações */}
                      <td>
                        {ip ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
                            <div style={{ display: 'flex', gap: '0.3rem' }}>
                              <button
                                type="button"
                                className="btn btn-secondary btn-sm"
                                style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}
                                title={`Baixar arquivo de conexão UltraVNC para ${ip}`}
                                onClick={() => baixarVnc(ip, a.nome_personalizado || a.hostname)}
                              >
                                <i className="bi bi-display" />
                                Conectar
                              </button>
                              <button
                                type="button"
                                className="btn btn-secondary btn-sm"
                                style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem' }}
                                title={`Ping ${ip}`}
                                disabled={pingando.has(a.hostname)}
                                onClick={() => executarPing(a.hostname, ip)}
                              >
                                {pingando.has(a.hostname)
                                  ? <i className="bi bi-hourglass-split" />
                                  : <i className="bi bi-reception-4" />}
                                Ping
                              </button>
                            </div>
                            {pings[a.hostname] !== undefined && pings[a.hostname] !== null && (
                              <span style={{
                                fontSize: '0.78rem', fontWeight: 600,
                                color: pings[a.hostname].alive ? '#4caf50' : '#e53935',
                              }}>
                                {pings[a.hostname].alive
                                  ? (pings[a.hostname].latency !== null
                                    ? `${pings[a.hostname].latency} ms`
                                    : 'Respondeu')
                                  : 'Sem resposta'}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="cell-muted" title="Nenhum IPv4 disponível">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
